import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { ThemeContext } from '../context/ThemeContext';
import NotificationBell from './NotificationBell';
import './Navbar.css';

const Navbar = () => {
  const { isAuthenticated, user, logout, isAdmin } = useContext(AuthContext);
  const { isDarkMode, toggleTheme } = useContext(ThemeContext);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Fetch unread message count if authenticated
    const fetchUnreadCount = async () => {
      if (isAuthenticated) {
        try {
          const response = await fetch('/api/messages/unread-count', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          const data = await response.json();
          if (data.success) {
            setUnreadMessages(data.data.unreadCount);
          }
        } catch (error) {
          console.error('Failed to fetch unread count:', error);
        }
      }
    };

    fetchUnreadCount();
    // Poll for updates every 2 minutes
    const interval = setInterval(fetchUnreadCount, 120000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  useEffect(() => {
    // Auto-hide navbar on scroll (mobile only)
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Only apply auto-hide on mobile/tablet screens
      if (window.innerWidth <= 768) {
        if (currentScrollY < lastScrollY || currentScrollY < 50) {
          // Scrolling up or near top - show navbar
          setIsVisible(true);
        } else if (currentScrollY > lastScrollY && currentScrollY > 150) {
          // Scrolling down and past threshold (increased from 100 to 150) - hide navbar
          setIsVisible(false);
        }
      } else {
        // Always show on desktop
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Search users with debouncing
  useEffect(() => {
    const searchUsers = async () => {
      if (searchQuery.trim().length < 2) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }

      setSearchLoading(true);
      try {
        const response = await fetch(`/api/profile/search?q=${encodeURIComponent(searchQuery)}`);
        const data = await response.json();
        setSearchResults(data.users || []);
        setShowSearchResults(true);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleSearchSelect = (username) => {
    navigate(`/profile/${username}`);
    setSearchQuery('');
    setShowSearchResults(false);
    setSearchResults([]);
  };

  const handleLogout = async () => {
    try {
      await logout();
      setIsDrawerOpen(false);
      navigate('/');
    } catch (error) {
      alert('Logout failed: ' + (error.response?.data?.message || 'Server error'));
    }
  };

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
  };

  const toggleDropdown = (dropdownName) => {
    setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
  };

  const closeDropdowns = () => {
    setActiveDropdown(null);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.nav-dropdown-container')) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Helper to determine if a route is active
  const isActive = (path) => location.pathname === path ? 'active' : '';

  return (
    <>
      <nav className={`navbar ${isVisible ? 'navbar-visible' : 'navbar-hidden'}`}>
        <div className="navbar-container">
          <Link to="/" className="navbar-brand" onClick={closeDrawer}>
            <span>🎮 GamersUnion</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="desktop-nav">
            <div className="nav-dropdown-container">
              <button className="nav-btn" onClick={() => toggleDropdown('social')}>
                ⚡ Social Hub ▼
              </button>
              <div className={`nav-dropdown ${activeDropdown === 'social' ? 'show' : ''}`}>
                <Link to="/" className="dropdown-item" onClick={closeDropdowns}>🏠 Home</Link>
                <Link to="/leaderboard" className="dropdown-item" onClick={closeDropdowns}>🏅 Leaderboard</Link>
                <Link to="/hall-of-fame" className="dropdown-item" onClick={closeDropdowns}>🏆 Hall of Fame</Link>
                <Link to="/news" className="dropdown-item" onClick={closeDropdowns}>🎮 Gaming News</Link>
                <Link to="/chat" className="dropdown-item" onClick={closeDropdowns}>💬 Chat Rooms</Link>
                {isAuthenticated && (
                  <>
                    <Link to="/shop" className="dropdown-item" onClick={closeDropdowns}>🛒 Shop</Link>
                    <Link to="/scammers" className="dropdown-item" onClick={closeDropdowns}>🛡️ Scammers</Link>
                  </>
                )}
              </div>
            </div>

            <div className="nav-dropdown-container">
              <button className="nav-btn" onClick={() => toggleDropdown('community')}>
                👥 Community Hub ▼
              </button>
              <div className={`nav-dropdown ${activeDropdown === 'community' ? 'show' : ''}`}>
                <Link to="/indie-games" className="dropdown-item" onClick={closeDropdowns}>🎮 Indie Games</Link>
                <Link to="/community" className="dropdown-item" onClick={closeDropdowns}>👥 Community Cards</Link>
                <Link to="/upcoming-releases" className="dropdown-item" onClick={closeDropdowns}>📅 Upcoming Releases</Link>
                <Link to="/discover-lists" className="dropdown-item" onClick={closeDropdowns}>📋 Discover Lists</Link>
                <Link to="/game-reviews" className="dropdown-item" onClick={closeDropdowns}>⭐ Game Reviews</Link>
                <Link to="/partners" className="dropdown-item" onClick={closeDropdowns}>🤝 Partners</Link>
              </div>
            </div>

            <div className="nav-dropdown-container">
              <button className="nav-btn" onClick={() => toggleDropdown('deals')}>
                🎁 Deals & Rewards ▼
              </button>
              <div className={`nav-dropdown ${activeDropdown === 'deals' ? 'show' : ''}`}>
                <Link to="/giveaways" className="dropdown-item" onClick={closeDropdowns}>🎁 Giveaways</Link>
                <Link to="/discounts" className="dropdown-item" onClick={closeDropdowns}>💰 Discounts</Link>
                <Link to="/code-requests" className="dropdown-item" onClick={closeDropdowns}>🎮 Code Requests</Link>
                <Link to="/points-guide" className="dropdown-item" onClick={closeDropdowns}>💰 Earn Points</Link>
              </div>
            </div>

            <div className="nav-dropdown-container">
              <button className="nav-btn" onClick={() => toggleDropdown('tools')}>
                🛠️ Tools ▼
              </button>
              <div className={`nav-dropdown ${activeDropdown === 'tools' ? 'show' : ''}`}>
                <Link to="/tools" className="dropdown-item" onClick={closeDropdowns}>🎮 Gaming Tools</Link>
                <Link to="/twitter-tools" className="dropdown-item" onClick={closeDropdowns}>🐦 Twitter Tools</Link>
                <Link to="/creator-tools" className="dropdown-item" onClick={closeDropdowns}>🎬 Creator Tools</Link>
              </div>
            </div>
          </div>

          {/* Search Bar (Desktop & Tablet) */}
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => searchResults.length > 0 && setShowSearchResults(true)}
              onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
            />
            <span className="search-icon">🔍</span>

            {showSearchResults && searchResults.length > 0 && (
              <div className="search-results">
                {searchLoading ? (
                  <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                    Searching...
                  </div>
                ) : (
                  searchResults.map((user) => (
                    <div
                      key={user._id}
                      className="search-result-item"
                      onClick={() => handleSearchSelect(user.username)}
                    >
                      {user.profile?.avatar?.url ? (
                        <img src={user.profile.avatar.url} alt={user.username} className="search-avatar" />
                      ) : (
                        <div className="search-avatar-placeholder">👤</div>
                      )}
                      <div>
                        <div style={{ fontWeight: 'bold', color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                          {user.username}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                          {user.accountType || 'Gamer'}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* User Controls & Mobile Toggle */}
          <div className="user-controls">
            <button className="theme-btn" onClick={toggleTheme} aria-label="Toggle Theme">
              {isDarkMode ? '☀️' : '🌙'}
            </button>

            {isAuthenticated ? (
              <>
                <div className="nav-notification-wrapper">
                  <NotificationBell />
                </div>

                <div className="nav-dropdown-container nav-user-menu">
                  <button className="user-btn" onClick={() => toggleDropdown('user')}>
                    👤 {user?.username}
                  </button>
                  <div className={`nav-dropdown ${activeDropdown === 'user' ? 'show' : ''}`} style={{ right: 0, left: 'auto' }}>
                    <Link to="/dashboard" className="dropdown-item" onClick={closeDropdowns}>📊 Dashboard</Link>
                    <Link to={`/profile/${user?.username}`} className="dropdown-item" onClick={closeDropdowns}>👤 My Profile</Link>
                    <Link to="/achievements" className="dropdown-item" onClick={closeDropdowns}>🏆 Achievements</Link>
                    <Link to="/feed" className="dropdown-item" onClick={closeDropdowns}>📰 My Feed</Link>
                    <Link to="/messages" className="dropdown-item" onClick={closeDropdowns}>
                      💬 Messages
                      {unreadMessages > 0 && <span className="dropdown-badge">{unreadMessages}</span>}
                    </Link>
                    <Link to="/settings" className="dropdown-item" onClick={closeDropdowns}>⚙️ Settings</Link>
                    {isAdmin && <Link to="/admin" className="dropdown-item" onClick={closeDropdowns}>🛡️ Admin</Link>}
                    <button onClick={() => { handleLogout(); closeDropdowns(); }} className="dropdown-item" style={{ width: '100%', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--error-text)' }}>
                      🚪 Logout
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="auth-buttons">
                <Link to="/login" className="user-btn" style={{ background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-primary)', boxShadow: 'none' }}>
                  Login
                </Link>
                <Link to="/register" className="user-btn">
                  Register
                </Link>
              </div>
            )}

            <button className="mobile-menu-toggle" onClick={toggleDrawer}>
              ☰
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Drawer Menu */}
      <div className={`mobile-drawer-overlay ${isDrawerOpen ? 'open' : ''}`} onClick={closeDrawer} />
      <div className={`mobile-drawer ${isDrawerOpen ? 'open' : ''}`}>
        <div className="drawer-header">
          {isAuthenticated ? (
            <div className="drawer-user-info">
              <div className="drawer-avatar-placeholder" style={{ fontSize: '2rem' }}>👤</div>
              <div>
                <span className="drawer-username">{user?.username}</span>
                <span className="drawer-role">{user?.accountType || 'Member'}</span>
              </div>
            </div>
          ) : (
            <div className="drawer-user-info">
              <span className="drawer-username">Guest Gamer</span>
            </div>
          )}
          <button className="drawer-close" onClick={closeDrawer}>✕</button>
        </div>

        <div className="drawer-content">
          {!isAuthenticated && (
            <div className="drawer-section">
              <h4 className="drawer-section-title">Join Us</h4>
              <Link to="/login" className="drawer-link" onClick={closeDrawer}>
                <span className="drawer-link-icon">🔐</span> Login
              </Link>
              <Link to="/register" className="drawer-link" onClick={closeDrawer}>
                <span className="drawer-link-icon">✨</span> Register
              </Link>
            </div>
          )}

          <div className="drawer-section">
            <h4 className="drawer-section-title">Navigation</h4>
            <Link to="/" className={`drawer-link ${isActive('/')}`} onClick={closeDrawer}>
              <span className="drawer-link-icon">🏠</span> Home
            </Link>
            <Link to="/leaderboard" className={`drawer-link ${isActive('/leaderboard')}`} onClick={closeDrawer}>
              <span className="drawer-link-icon">🏅</span> Leaderboard
            </Link>
            <Link to="/hall-of-fame" className={`drawer-link ${isActive('/hall-of-fame')}`} onClick={closeDrawer}>
              <span className="drawer-link-icon">🏆</span> Hall of Fame
            </Link>
            <Link to="/news" className={`drawer-link ${isActive('/news')}`} onClick={closeDrawer}>
              <span className="drawer-link-icon">🎮</span> News
            </Link>
          </div>

          <div className="drawer-section">
            <h4 className="drawer-section-title">Community Hub</h4>
            <Link to="/indie-games" className={`drawer-link ${isActive('/indie-games')}`} onClick={closeDrawer}>
              <span className="drawer-link-icon">🎮</span> Indie Games
            </Link>
            <Link to="/community" className={`drawer-link ${isActive('/community')}`} onClick={closeDrawer}>
              <span className="drawer-link-icon">👥</span> Community Cards
            </Link>
            <Link to="/upcoming-releases" className={`drawer-link ${isActive('/upcoming-releases')}`} onClick={closeDrawer}>
              <span className="drawer-link-icon">📅</span> Upcoming Releases
            </Link>
            <Link to="/discover-lists" className={`drawer-link ${isActive('/discover-lists')}`} onClick={closeDrawer}>
              <span className="drawer-link-icon">📋</span> Discover Lists
            </Link>
            <Link to="/game-reviews" className={`drawer-link ${isActive('/game-reviews')}`} onClick={closeDrawer}>
              <span className="drawer-link-icon">⭐</span> Game Reviews
            </Link>
            <Link to="/partners" className={`drawer-link ${isActive('/partners')}`} onClick={closeDrawer}>
              <span className="drawer-link-icon">🤝</span> Partners
            </Link>
          </div>

          <div className="drawer-section">
            <h4 className="drawer-section-title">Rewards & Deals</h4>
            <Link to="/giveaways" className={`drawer-link ${isActive('/giveaways')}`} onClick={closeDrawer}>
              <span className="drawer-link-icon">🎁</span> Giveaways
            </Link>
            <Link to="/discounts" className={`drawer-link ${isActive('/discounts')}`} onClick={closeDrawer}>
              <span className="drawer-link-icon">💰</span> Discounts
            </Link>
            <Link to="/code-requests" className={`drawer-link ${isActive('/code-requests')}`} onClick={closeDrawer}>
              <span className="drawer-link-icon">🎮</span> Code Requests
            </Link>
            <Link to="/points-guide" className={`drawer-link ${isActive('/points-guide')}`} onClick={closeDrawer}>
              <span className="drawer-link-icon">💰</span> Earn Points
            </Link>
          </div>

          <div className="drawer-section">
            <h4 className="drawer-section-title">Tools</h4>
            <Link to="/tools" className={`drawer-link ${isActive('/tools')}`} onClick={closeDrawer}>
              <span className="drawer-link-icon">🎮</span> Gaming Tools
            </Link>
            <Link to="/twitter-tools" className={`drawer-link ${isActive('/twitter-tools')}`} onClick={closeDrawer}>
              <span className="drawer-link-icon">🐦</span> Twitter Tools
            </Link>
            <Link to="/creator-tools" className={`drawer-link ${isActive('/creator-tools')}`} onClick={closeDrawer}>
              <span className="drawer-link-icon">🎬</span> Creator Tools
            </Link>
          </div>

          {isAuthenticated && (
            <div className="drawer-section">
              <h4 className="drawer-section-title">My Account</h4>
              <Link to="/dashboard" className={`drawer-link ${isActive('/dashboard')}`} onClick={closeDrawer}>
                <span className="drawer-link-icon">📊</span> Dashboard
              </Link>
              <Link to={`/profile/${user?.username}`} className={`drawer-link ${isActive(`/profile/${user?.username}`)}`} onClick={closeDrawer}>
                <span className="drawer-link-icon">👤</span> My Profile
              </Link>
              <Link to="/messages" className={`drawer-link ${isActive('/messages')}`} onClick={closeDrawer}>
                <span className="drawer-link-icon">💬</span> Messages
                {unreadMessages > 0 && <span className="dropdown-badge" style={{ marginLeft: 'auto' }}>{unreadMessages}</span>}
              </Link>
              <Link to="/settings" className={`drawer-link ${isActive('/settings')}`} onClick={closeDrawer}>
                <span className="drawer-link-icon">⚙️</span> Settings
              </Link>
              {isAdmin && <Link to="/admin" className={`drawer-link ${isActive('/admin')}`} onClick={closeDrawer}>
                <span className="drawer-link-icon">🛡️</span> Admin
              </Link>}
            </div>
          )}
        </div>

        <div className="drawer-footer">
          {isAuthenticated && (
            <button
              onClick={handleLogout}
              style={{ width: '100%', padding: '12px', background: 'var(--error-bg)', color: 'var(--error-text)', border: 'none', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer' }}
            >
              Log Out
            </button>
          )}
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="mobile-bottom-nav">
        <Link to="/" className={`bottom-nav-item ${isActive('/')}`}>
          <span className="icon">🏠</span>
          <span>Home</span>
        </Link>
        <Link to="/community" className={`bottom-nav-item ${isActive('/community')}`}>
          <span className="icon">👥</span>
          <span>Social</span>
        </Link>
        <Link to="/giveaways" className={`bottom-nav-item ${isActive('/giveaways')}`}>
          <span className="icon">🎁</span>
          <span>Rewards</span>
        </Link>
        {isAuthenticated ? (
          <Link to="/dashboard" className={`bottom-nav-item ${isActive('/dashboard')}`}>
            <span className="icon">📊</span>
            <span>Stats</span>
          </Link>
        ) : (
          <Link to="/login" className={`bottom-nav-item ${isActive('/login')}`}>
            <span className="icon">🔐</span>
            <span>Login</span>
          </Link>
        )}
        <div className="bottom-nav-item" onClick={toggleDrawer} style={{ cursor: 'pointer' }}>
          <span className="icon">☰</span>
          <span>Menu</span>
        </div>
      </div>
    </>
  );
};

export default Navbar;
